import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../utils/api";
import { Agent, AgentListResponse } from "../../types/agent";

interface UseAgentListParams {
  category?: string;
  pageSize?: number;
}

interface UseAgentListReturn {
  agents: Agent[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string;
  hasMore: boolean;
  fetchAgents: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

const useAgentList = (params: UseAgentListParams = {}): UseAgentListReturn => {
  const { category = "all", pageSize = 15 } = params;
  
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchAgents = useCallback(async (page: number = 1, isAppend: boolean = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError("");

    try {
      const queryParams: Record<string, string | number> = {
        page: page,
      };

      if (category && category !== "all") {
        queryParams.ct = category;
      }

      const response = await apiClient.get("/api/v1/agent/list", { 
        params: queryParams 
      });
      
      const responseData: AgentListResponse = response.data;

      if (responseData.status) {
        const newAgents = responseData.data;
        
        if (isAppend) {
          setAgents(prev => [...prev, ...newAgents]);
        } else {
          setAgents(newAgents);
        }

        // 페이지 크기보다 적게 받았으면 더 이상 데이터가 없음
        setHasMore(newAgents.length >= pageSize);
        setCurrentPage(page);
      } else {
        setError("에이전트 목록을 불러오는데 실패했습니다.");
      }
    } catch (err) {
      console.error("에이전트 목록 조회 오류:", err);
      setError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [pageSize, category]);

  const loadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore && !isLoading) {
      await fetchAgents(currentPage + 1, true);
    }
  }, [hasMore, isLoadingMore, isLoading, currentPage, fetchAgents]);

  const refresh = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await fetchAgents(1, false);
  }, [fetchAgents]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchAgents(1, false);
  }, [fetchAgents]);

  return {
    agents,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    fetchAgents: () => fetchAgents(1, false),
    loadMore,
    refresh,
  };
};

export default useAgentList;
export type { UseAgentListParams, UseAgentListReturn }; 